export const SHELL_SOURCE = 'claudafinil-shell' as const;
export const REMOTE_SOURCE = 'claudafinil-remote' as const;
export const CONTRACT_VERSION = 2 as const;
export const CONTRACT_SUPPORTED_VERSIONS = [1, 2] as const;

export interface ShellMessage<
  TType extends string = string,
  TPayload = unknown,
> {
  source: typeof SHELL_SOURCE;
  type: TType;
  version: number;
  requestId?: string;
  payload: TPayload;
}

export interface RemoteMessage<
  TType extends string = string,
  TPayload = unknown,
> {
  source: typeof REMOTE_SOURCE;
  type: TType;
  version: number;
  requestId?: string;
  payload: TPayload;
}

/**
 * Contract v1 compatibility policy:
 * - Existing required selector/endpoint keys are stable.
 * - New selector/endpoint keys must be additive and optional.
 * - Consumers must ignore unknown optional keys.
 */
export interface AdapterSelectorConfig {
  chatInput: string;
  messageContainer: string;
  messageIdAttribute: string;
  [selectorKey: string]: string;
}

export interface AdapterEndpointConfig {
  conversationGet: string;
  branchSwitch: string;
  [endpointKey: string]: string;
}

export type AdapterParsingConfig = Record<string, unknown>;

export interface AdapterConfig {
  platform: 'claude' | 'chatgpt';
  version: string;
  selectors: AdapterSelectorConfig;
  endpoints: AdapterEndpointConfig;
  parsing?: AdapterParsingConfig;
}

export interface HandshakeAckPayload {
  selectedVersion: number;
  platform?: AdapterConfig['platform'];
  /**
   * Added in contract v2. The Chrome extension's manifest version
   * (e.g. "0.1.5"). Older shells may omit this field.
   */
  extensionVersion?: string;
}

export interface HandshakeRejectPayload {
  shellVersion: number;
  minSupported: number;
}

export interface CurrentLeafUpdatedPayload {
  leafId: string;
}

export interface AuthStatePayload {
  isAuthenticated: boolean;
  user: object | null;
  subscription: AuthSubscriptionPayload | null;
}

export interface AuthTokenPayload {
  accessToken: string;
}

/**
 * @deprecated Pro tier removed; all authenticated users have full access.
 * `isPro` is always `true` and the status/date fields are always `null`.
 * Retained on the v1/v2 contract so older shipped extensions keep working.
 * Drop in the next contract version bump.
 */
export interface AuthSubscriptionPayload {
  isPro: boolean;
  status: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
}

export interface NavigateResultPayload {
  success: boolean;
  error?: string;
}

export interface PromptInjectResultPayload {
  success: boolean;
  error?: string;
}

export interface ConfigAckPayload {
  applied: boolean;
}

export interface RefreshConversationResultPayload {
  success: boolean;
  error?: string;
}

export interface HandshakePayload {
  supportedVersions: number[];
}

export interface NavigateToNodePayload {
  nodeId: string;
  conversationId?: string;
  messageText?: string;
  platform?: AdapterConfig['platform'];
}

export interface InjectPromptPayload {
  text: string;
  variables?: Record<string, string>;
}

export interface ConversationMessagePayload {
  uuid: string;
  parentUuid: string | null;
  sender: 'human' | 'assistant';
  text: string;
  createdAt: string | null;
}

export interface ConversationDataPayload {
  id: string;
  name: string;
  currentLeafId: string;
  messages: ConversationMessagePayload[];
}

export type EmptyPayload = Record<string, never>;

export type HandshakeAckMessage = ShellMessage<
  'HANDSHAKE_ACK',
  HandshakeAckPayload
>;
export type HandshakeRejectMessage = ShellMessage<
  'HANDSHAKE_REJECT',
  HandshakeRejectPayload
>;
export type ConversationDataMessage = ShellMessage<
  'CONVERSATION_DATA',
  ConversationDataPayload
>;
export type CurrentLeafUpdatedMessage = ShellMessage<
  'CURRENT_LEAF_UPDATED',
  CurrentLeafUpdatedPayload
>;
export type AuthStateMessage = ShellMessage<'AUTH_STATE', AuthStatePayload>;
export type AuthTokenMessage = ShellMessage<'AUTH_TOKEN', AuthTokenPayload>;
export type NavigateResultMessage = ShellMessage<
  'NAVIGATE_RESULT',
  NavigateResultPayload
>;
export type PromptInjectResultMessage = ShellMessage<
  'PROMPT_INJECT_RESULT',
  PromptInjectResultPayload
>;
export type ConfigAckMessage = ShellMessage<'CONFIG_ACK', ConfigAckPayload>;
export type RefreshConversationResultMessage = ShellMessage<
  'REFRESH_CONVERSATION_RESULT',
  RefreshConversationResultPayload
>;

export type ShellToRemoteMessage =
  | HandshakeAckMessage
  | HandshakeRejectMessage
  | ConversationDataMessage
  | CurrentLeafUpdatedMessage
  | AuthStateMessage
  | AuthTokenMessage
  | NavigateResultMessage
  | PromptInjectResultMessage
  | ConfigAckMessage
  | RefreshConversationResultMessage;

export type HandshakeMessage = RemoteMessage<'HANDSHAKE', HandshakePayload>;
export type ReadyMessage = RemoteMessage<'READY', EmptyPayload>;
export type NavigateToNodeMessage = RemoteMessage<
  'NAVIGATE_TO_NODE',
  NavigateToNodePayload
>;
export type InjectPromptMessage = RemoteMessage<
  'INJECT_PROMPT',
  InjectPromptPayload
>;
export type AuthSignInMessage = RemoteMessage<'AUTH_SIGN_IN', EmptyPayload>;
export type AuthSignOutMessage = RemoteMessage<'AUTH_SIGN_OUT', EmptyPayload>;
export type AuthTokenRequestMessage = RemoteMessage<
  'AUTH_TOKEN_REQUEST',
  EmptyPayload
>;
export type AdapterConfigMessage = RemoteMessage<'ADAPTER_CONFIG', AdapterConfig>;
export type RefreshConversationMessage = RemoteMessage<
  'REFRESH_CONVERSATION',
  EmptyPayload
>;

export type RemoteToShellMessage =
  | HandshakeMessage
  | ReadyMessage
  | NavigateToNodeMessage
  | InjectPromptMessage
  | AuthSignInMessage
  | AuthSignOutMessage
  | AuthTokenRequestMessage
  | AdapterConfigMessage
  | RefreshConversationMessage;

export type AnyContractMessage = ShellToRemoteMessage | RemoteToShellMessage;

export type MessageByType<
  TMessage extends { type: string },
  TType extends TMessage['type'],
> = Extract<TMessage, { type: TType }>;

export type PayloadByType<
  TMessage extends { type: string; payload: unknown },
  TType extends TMessage['type'],
> = MessageByType<TMessage, TType>['payload'];
